import {Component, effect, OnInit, signal} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {CodaltComponent} from '../codalt.component';
import {ActivatedRoute, Router} from '@angular/router';
import {Platform} from '../classes/platform';
import {Utils} from '../utils.class';
import {PlatformService} from '../services/platform.service';
import {MatInput} from '@angular/material/input';
import {MatFormField, MatHint, MatSuffix} from '@angular/material/form-field';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatButton} from '@angular/material/button';
import {FieldErrorComponent} from '../field-error/field-error.component';
import {Company} from '../classes/company';
import {CompanyService} from '../services/company.service';
import {NgSelectComponent} from '@ng-select/ng-select';
import {Routenames} from '../classes/route-name.enum';
import {ConfirmDialogService} from '../services/confirm-dialog-service/confirm-dialog.service';
import {ServerService} from '../services/server.service';
import {Server} from '../classes/server';
import {HasFeaturePipe} from '../pipes/has-feature.pipe';
import {AzureFeature, AzureLinkedFeature} from '../classes/azurefeature';
import {AuthorisationService} from '../services/auth/authorisation.service';
import {HasFunctionalityPipe} from '../pipes/has-functionality.pipe';
import {
    BreadcrumbsComponent,
    DataListComponent,
    DataRowComponent,
    FieldComponent,
    PageComponent,
    SectionComponent,
    StickySaveBarComponent
} from '../shared/ui';

/** Rows of the environments table; `key` is the prefix of the `<key>_url` and `<key>_server_ip` controls. */
const ENVIRONMENTS = [
    {key: 'prod', label: 'Productie', placeholder: 'https://www.voorbeeld.nl', hasServer: true},
    {key: 'acc', label: 'Acceptatie', placeholder: 'https://acc.voorbeeld.nl', hasServer: true},
    {key: 'test', label: 'Test', placeholder: 'https://test.voorbeeld.nl', hasServer: true},
    {key: 'local', label: 'Local', placeholder: 'http://localhost:8080', hasServer: false},
] as const;

@Component({
    selector: 'app-platform-edit',
    templateUrl: './platform-edit.component.html',
    styleUrls: ['./platform-edit.component.scss'],
    imports: [
        MatDatepickerToggle,
        MatDatepicker,
        MatDatepickerInput,
        MatInput,
        MatFormField,
        MatHint,
        MatSuffix,
        FormsModule,
        ReactiveFormsModule,
        FieldErrorComponent,
        NgSelectComponent,
        NgTemplateOutlet,
        MatButton,
        HasFeaturePipe,
        HasFunctionalityPipe,
        BreadcrumbsComponent,
        DataListComponent,
        DataRowComponent,
        FieldComponent,
        PageComponent,
        SectionComponent,
        StickySaveBarComponent
    ]
})

export class PlatformEditComponent extends CodaltComponent implements OnInit {
    readonly form = signal<FormGroup>(null);
    readonly companies = signal<Company[]>([]);
    readonly servers = signal<Server[]>([]);
    readonly azureFeatures = signal<AzureFeature[]>(null);
    /** Dirty/valid state for the sticky save bar, refreshed on every form event (also from the datepicker overlay). */
    readonly formState = signal({dirty: false, valid: true});
    readonly environments = ENVIRONMENTS;

    minDate: Date;

    constructor(
        private activatedRoute: ActivatedRoute,
        private companyService: CompanyService,
        private confirmDialog: ConfirmDialogService,
        private platformService: PlatformService,
        private router: Router,
        private serverService: ServerService,
    ) {
        super();
        effect(onCleanup => {
            const form = this.form();
            if (!form) {
                return;
            }
            const update = () => this.formState.set({dirty: form.dirty, valid: form.valid});
            update();
            const subscription = form.events.subscribe(update);
            onCleanup(() => subscription.unsubscribe());
        });
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.companyService.getCompanies().subscribe(companies => {
                this.companies.set(companies);
            })
        );

        this.subscriptions.add(
            this.serverService.getServers().subscribe(servers => {
                this.servers.set(servers);
            })
        );

        this.subscriptions.add(this.activatedRoute.params.subscribe((params: { id }) => {
            if (params.id === 'new') {
                this.createForm(new Platform());
            } else {
                this.subscriptions.add(
                    this.platformService.getPlatform(+params.id).subscribe({
                        next: res => this.createForm(res),
                        error: err => console.error(err)
                    })
                );
            }
        }));
    }

    createForm(platform: Platform) {
        this.crumbs = [
            {name: 'Seire', route: Routenames.seire},
            {name: `Platform : ${platform.name || 'nieuw'}`, route: `${Routenames.platforms}/${platform.id || 'new'}`}
        ];

        const startDate = new Date();
        Utils.setTime(startDate, 0, 0, 0);
        const endDate = new Date(startDate);
        Utils.setTime(endDate, 23, 59, 0);

        if (!platform.id || !Utils.isPast(platform.start_date)) {
            this.minDate = new Date(startDate);
            this.minDate.setDate(this.minDate.getDate() - 100);
        } else {
            this.minDate = new Date(platform.start_date);
        }

        this.form.set(new FormGroup({
            id: new FormControl(platform.id),
            company_id: new FormControl(platform.company_id, Validators.required),
            responsible_user_id: new FormControl(platform.responsible_user_id),
            fallback_responsible_user_id: new FormControl(platform.fallback_responsible_user_id),
            name: new FormControl<string>(platform.name, Validators.required),
            prod_url: new FormControl(platform.prod_url),
            prod_server_ip: new FormControl(platform.prod_server_ip),
            acc_url: new FormControl(platform.acc_url),
            acc_server_ip: new FormControl(platform.acc_server_ip),
            test_url: new FormControl(platform.test_url),
            test_server_ip: new FormControl(platform.test_server_ip),
            local_url: new FormControl(platform.local_url),
            start_date: new FormControl(platform.start_date ? new Date(platform.start_date) : startDate, Validators.required),
            end_date: new FormControl(platform.end_date ? new Date(platform.end_date) : platform.end_date),
            description: new FormControl(platform.description),
            software_info: new FormControl(platform.software_info),
            azure_features: new FormControl(platform.azure_features?.map(f => f.azure_feature_id) ?? []),
            updated_at: new FormControl(platform.updated_at)
        }));
        if (this.form()?.get('id').value && AuthorisationService.hasFunctionality(this.Functionalities.Financials)) {
            this.loadAzureItems();
        }
    }

    loadAzureItems() {
        this.subscriptions.add(
            this.companyService.getAzureFeatures(this.form()?.get('company_id').value).subscribe((items) => {
                this.azureFeatures.set(items);
            })
        );
    }

    save() {
        Utils.triggerValidation(this.form());
        if (this.form().valid) {
            const platform = new Platform();
            Object.assign(platform, this.form().value);
            platform.azure_features = (this.form().value.azure_features as number[])
                ?.map(id => ({platform_id: platform.id, azure_feature_id: id}) as AzureLinkedFeature);
            this.subscriptions.add(this.platformService.savePlatform(platform).subscribe(() => {
                this.router.navigate([Routenames.seire]);
            }));
        }
    }

    cancel() {
        this.router.navigate([Routenames.seire]);
    }

    openDelete(event) {
        event.preventDefault();
        event.stopPropagation();

        const platform = new Platform();
        Object.assign(platform, this.form().value);

        this.confirmDialog.confirm(
            'Verwijderen',
            `Weet u zeker dat u ${platform.name} wilt verwijderen?`,
            'Verwijderen',
            'Behouden'
        )
            .then(() => {
                this.platformService.deletePlatform(platform.id)
                    .then(() => {
                        this.router.navigate([Routenames.seire]);
                    })
            })
            .catch(() => {
                console.log('platform not deleted')
            });
    }
}
